import { IPortalPaymentMethod, ISenderDetail } from "shared";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BuildingLibraryIcon, CreditCardIcon } from "@heroicons/react/24/outline";

import { Badge } from "../../../common/Atoms/Badge";
import { Button } from "../../../common/Atoms/Button";
import { Card } from "../../../common/Atoms/Card";
import { CardContent } from "../../../common/Atoms/CardContent";
import { Modal } from "../../../common/Atoms/Modal";
import { MultiButtonCardHeader } from "../../../common/Atoms/MultiButtonCardHeader";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { classNames } from "../../../common/lib/classNames";
import { useSession } from "../../hooks/useSession";
import {
  PaymentMethodResourceType,
  useCreatePaymentMethodSetupSessionMutation,
  useFetchPaymentMethodsQuery,
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
} from "../../services/api/paymentMethods";
import { StripeSetupCheckout } from "../Stripe/StripeSetupCheckout";
import { Verify } from "../Verify";

interface PaymentMethodsCardProps {
  resourceType: PaymentMethodResourceType;
  code: string;
  stripeConnectAccount: string;
  senderDetail: ISenderDetail;
  /** Section for the invoice page pay rail instead of a full card */
  compact?: boolean;
}

function expiry(pm: IPortalPaymentMethod) {
  if (!pm.expMonth || !pm.expYear) {
    return null;
  }

  return `Expires ${String(pm.expMonth).padStart(2, `0`)}/${pm.expYear}`;
}

export function PaymentMethodsCard({ resourceType, code, stripeConnectAccount, senderDetail, compact }: PaymentMethodsCardProps) {
  const session = useSession();
  const args = { resourceType, code, sessionId: session?.id ?? `` };
  const { data, isLoading } = useFetchPaymentMethodsQuery(args, { skip: !session?.id });
  const [createSetupSession, { isLoading: creatingSetup }] = useCreatePaymentMethodSetupSessionMutation();
  const [removePaymentMethod, { isLoading: removing }] = useRemovePaymentMethodMutation();
  const [setDefaultPaymentMethod, { isLoading: settingDefault }] = useSetDefaultPaymentMethodMutation();
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<IPortalPaymentMethod | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const requiresVerification = !!data?.requiresVerification;

  useEffect(() => {
    if (!requiresVerification) {
      setVerifyOpen(false);
    }
  }, [requiresVerification]);

  async function onAdd() {
    try {
      const { clientSecret } = await createSetupSession(args).unwrap();
      setSetupClientSecret(clientSecret);
    }
    catch {
      toast.error(`Could not start adding a payment method`);
    }
  }

  async function onSetDefault(pm: IPortalPaymentMethod) {
    try {
      await setDefaultPaymentMethod({ ...args, paymentMethodId: pm.id }).unwrap();
      toast.success(`Default payment method updated`);
    }
    catch {
      toast.error(`Could not update default payment method`);
    }
  }

  async function onConfirmRemove() {
    if (!pendingRemove) {
      return;
    }

    try {
      await removePaymentMethod({ ...args, paymentMethodId: pendingRemove.id }).unwrap();
      toast.success(`Payment method removed`);
      setPendingRemove(null);
    }
    catch {
      toast.error(`Could not remove payment method`);
    }
  }

  const paymentMethods = data?.paymentMethods ?? [];

  const modals = (
    <>
      { setupClientSecret && (
        <StripeSetupCheckout
          clientSecret={ setupClientSecret }
          connectedAccountId={ stripeConnectAccount }
          onClose={ () => setSetupClientSecret(null) }
          description={ `Save a payment method for future payments. Surcharges may apply` }
        />
      ) }

      <Modal
        open={ !!pendingRemove }
        setOpen={ () => setPendingRemove(null) }
        title={ `Remove payment method` }
      >
        <Paragraph className={ `mb-4` }>
          { `Remove ` }
          <span className={ `font-medium` }>{ pendingRemove?.label }</span>
          { `?` }
        </Paragraph>
        <div className={ `flex justify-end gap-2` }>
          <Button
            color={ `gray` }
            onClick={ () => setPendingRemove(null) }
          >
            { `Cancel` }
          </Button>
          <Button
            color={ `red` }
            loading={ removing }
            onClick={ onConfirmRemove }
          >
            { `Remove` }
          </Button>
        </div>
      </Modal>

      <Modal
        open={ verifyOpen }
        setOpen={ setVerifyOpen }
        title={ `` }
      >
        <Verify
          resourceType={ resourceType }
          senderDetail={ data?.senderDetail ?? senderDetail }
          generatedRedactedEmailList={ data?.generatedRedactedEmailList ?? [] }
          verificationMessage={ `You need to verify your session before managing payment methods.` }
          inline
        />
      </Modal>
    </>
  );

  if (compact) {
    const linkClass = `cursor-pointer font-medium text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline disabled:opacity-50`;

    return (
      <div className={ `flex flex-col gap-1.5 border-t border-gray-100 px-5 py-3.5` }>
        <div className={ `flex items-center justify-between gap-2` }>
          <span className={ `text-[0.8125rem] font-semibold text-gray-900` }>{ `Saved payment methods` }</span>
          <button
            type={ `button` }
            className={ classNames(`text-[0.8125rem]`, linkClass) }
            onClick={ requiresVerification ? () => setVerifyOpen(true) : onAdd }
            disabled={ isLoading || creatingSetup }
          >
            { requiresVerification ? `Verify to add` : `+ Add` }
          </button>
        </div>
        { isLoading && (
          <span className={ `text-[0.8125rem] text-gray-500` }>{ `Loading payment methods...` }</span>
        ) }
        { requiresVerification && (
          <span className={ `text-[0.8125rem] text-gray-500` }>{ `Verify your email address to add or manage saved payment methods.` }</span>
        ) }
        { !isLoading && !requiresVerification && paymentMethods.length === 0 && (
          <span className={ `text-[0.8125rem] text-gray-500` }>{ `No saved payment methods.` }</span>
        ) }
        { paymentMethods.map(pm => {
          const Icon = pm.type === `card` ? CreditCardIcon : BuildingLibraryIcon;

          return (
            <div
              key={ pm.id }
              className={ `flex items-center gap-2.5 py-1.5 text-sm` }
            >
              <span className={ `flex h-6 w-[2.125rem] shrink-0 items-center justify-center rounded-[0.3125rem] border border-gray-200 text-paidniceGreen-600` }>
                <Icon className={ `h-3.5 w-3.5` } />
              </span>
              <span className={ `flex min-w-0 flex-1 flex-col` }>
                <span className={ `truncate font-medium text-gray-900` }>{ pm.label }</span>
                { expiry(pm) && <span className={ `text-xs text-gray-500` }>{ expiry(pm) }</span> }
              </span>
              { pm.isDefault ? (
                <span className={ `rounded bg-gray-100 px-1.5 py-px text-[0.6875rem] font-medium text-gray-600` }>{ `Default` }</span>
              ) : (
                <button
                  type={ `button` }
                  className={ classNames(`text-xs`, linkClass) }
                  disabled={ settingDefault }
                  onClick={ () => onSetDefault(pm) }
                >
                  { `Make default` }
                </button>
              ) }
              <button
                type={ `button` }
                className={ `cursor-pointer text-xs text-paidniceRed-700 hover:underline` }
                onClick={ () => setPendingRemove(pm) }
              >
                { `Remove` }
              </button>
            </div>
          );
        }) }
        { modals }
      </div>
    );
  }

  return (
    <Card className={ `mt-6` }>
      <MultiButtonCardHeader
        title={ `Payment methods` }
        description={ `Saved payment methods for ${senderDetail.customerFacingCompanyName}` }
        buttons={ requiresVerification ? [{
          buttonText: `Verify to add payment method`,
          onClick: () => setVerifyOpen(true),
        }] : [{
          buttonText: `Add payment method`,
          onClick: onAdd,
          loading: creatingSetup,
          disabled: isLoading,
        }] }
      />
      <CardContent className={ `py-4` }>
        { isLoading && (
          <Paragraph variant={ `secondary` }>{ `Loading payment methods...` }</Paragraph>
        ) }
        { requiresVerification && (
          <Paragraph variant={ `secondary` }>
            { `Verify your email address to add or manage saved payment methods.` }
          </Paragraph>
        ) }
        { !isLoading && !requiresVerification && paymentMethods.length === 0 && (
          <Paragraph variant={ `secondary` }>{ `No saved payment methods.` }</Paragraph>
        ) }
        { paymentMethods.length > 0 && (
          <ul className={ `divide-y divide-gray-200` }>
            { paymentMethods.map(pm => (
              <li
                key={ pm.id }
                className={ `flex flex-wrap items-center justify-between gap-2 py-3` }
              >
                <div className={ `flex items-center gap-2` }>
                  <span className={ `text-sm font-medium text-gray-900` }>{ pm.label }</span>
                  { expiry(pm) && <span className={ `text-sm text-gray-500` }>{ expiry(pm) }</span> }
                  { pm.isDefault && <Badge color={ `default` }
                    message={ `Default` } /> }
                </div>
                <div className={ `flex gap-2` }>
                  { !pm.isDefault && (
                    <Button
                      size={ `sm` }
                      color={ `gray` }
                      loading={ settingDefault }
                      onClick={ () => onSetDefault(pm) }
                    >
                      { `Make default` }
                    </Button>
                  ) }
                  <Button
                    size={ `sm` }
                    color={ `paidniceRedOutline` }
                    onClick={ () => setPendingRemove(pm) }
                  >
                    { `Remove` }
                  </Button>
                </div>
              </li>
            )) }
          </ul>
        ) }
      </CardContent>

      { modals }
    </Card>
  );
}
